import { useEffect, useState, useCallback } from "react";
import api, { money, catIcon } from "../lib/api";
import { Receipt, CalendarClock, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import PayDialog from "../components/PayDialog";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Bills() {
  const { refresh } = useAuth();
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [payBill, setPayBill] = useState(null);
  const sym = "$";

  const load = useCallback(async () => {
    const [b, c] = await Promise.all([api.get("/bills"), api.get("/categories")]);
    setBills(b.data); setCategories(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.delete(`/bills/${id}`); toast.success("Bill removed"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">My Bills</h1>
        <p className="text-slate-500 mt-1">All your bills across categories.</p>
      </div>

      {bills.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center">
          <Receipt className="w-10 h-10 text-slate-300 mx-auto" />
          <p className="font-display font-bold text-slate-900 mt-4">No bills yet</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {bills.map((b) => {
            const Icon = catIcon(categories.find((c) => c.slug === b.category_slug)?.icon);
            const isPaid = b.status === "paid";
            return (
              <div key={b.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`bill-row-${b.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center"><Icon className="w-4 h-4 text-slate-700" /></div>
                    <div>
                      <p className="font-display font-bold text-slate-900 leading-tight">{b.category_name}</p>
                      <p className="text-xs text-slate-400">{b.provider_name}</p>
                    </div>
                  </div>
                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${isPaid ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{b.status}</span>
                </div>
                <p className="font-display text-2xl font-extrabold text-slate-900 mt-3">{money(b.amount, sym)}</p>
                {b.account_number && <p className="text-xs text-slate-400 mt-1">Acct: {b.account_number}</p>}
                {b.due_date && <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><CalendarClock className="w-3.5 h-3.5" /> Due {b.due_date}</p>}
                <div className="flex items-center gap-2 mt-4">
                  {!isPaid ? (
                    <Button onClick={() => setPayBill(b)} data-testid={`pay-btn-${b.id}`} className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600 font-semibold">PAY & SAVE</Button>
                  ) : (
                    <span className="flex-1 text-sm font-semibold text-emerald-600">✓ Paid</span>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => del(b.id)} data-testid={`del-btn-${b.id}`} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PayDialog open={!!payBill} onOpenChange={(o) => !o && setPayBill(null)} bill={payBill} onPaid={() => { load(); refresh(); }} />
    </div>
  );
}
